import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import InventoryAtPointSummary from '../../components/InventoryAtPointSummary'
import { formatTenantDateTime, formatTenantTime } from '../../lib/dates'

interface Shift {
  id: string; status: string; openedAt: string
  point: { id: string; name: string; address?: string }
  openGeoValid?: boolean; cashAtOpen?: string
}
interface Point { id: string; name: string }

const TYPE_LABELS: Record<string, string> = {
  adult: 'Взрослый', child: 'Детский', electric: 'Электро',
  lock: 'Замок', childSeat: 'Кресло', cart: 'Тележка',
}

export default function ShiftPage() {
  const qc = useQueryClient()
  const [selectedPointId, setSelectedPointId] = useState('')
  const [geoError, setGeoError] = useState<string | null>(null)
  const [step, setStep] = useState<'select' | 'open-confirm' | 'close-confirm'>('select')
  const [cashInput, setCashInput] = useState('')
  const [pointCash, setPointCash] = useState<number | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [geoLoading, setGeoLoading] = useState(false)
  const shiftActionRef = useRef(false)

  const { data: shiftData, isLoading } = useQuery({
    queryKey: ['shift', 'current'],
    queryFn: () => api.get('/shifts/current').then((r) => r.data),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const { data: pointsData } = useQuery({
    queryKey: ['points'],
    queryFn: () => api.get('/points').then((r) => r.data),
    enabled: !shiftData?.shift,
    refetchOnMount: 'always',
  })

  // Open shift preview (bikes + bookings at point)
  const { data: openPreview, isFetching: previewLoading } = useQuery({
    queryKey: ['shift-open-preview', selectedPointId],
    queryFn: () => api.get('/shifts/open-preview', { params: { pointId: selectedPointId } }).then((r) => r.data),
    enabled: step === 'open-confirm' && !!selectedPointId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 20_000,
    refetchInterval: step === 'open-confirm' && selectedPointId ? 30_000 : false,
  })

  // Cash from last closed shift at point
  const { data: pointCashData } = useQuery<{ cashBalance: number }>({
    queryKey: ['point-cash', selectedPointId],
    queryFn: () => api.get('/shifts/point-cash', { params: { pointId: selectedPointId } }).then((r) => r.data),
    enabled: step === 'open-confirm' && !!selectedPointId,
  })

  useEffect(() => {
    if (pointCashData) setPointCash(pointCashData.cashBalance)
  }, [pointCashData])

  // Close shift preview (revenue summary)
  const { data: closePreview, isFetching: closePreviewLoading } = useQuery({
    queryKey: ['shift-close-preview'],
    queryFn: () => api.get('/shifts/close-preview').then((r) => r.data),
    enabled: step === 'close-confirm',
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 20_000,
    refetchInterval: step === 'close-confirm' ? 30_000 : false,
  })

  const openShift = useMutation({
    mutationFn: (payload: { pointId: string; geo?: { lat: number; lng: number }; cashAtOpen?: number }) =>
      api.post('/shifts/open', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift', 'current'] })
      qc.invalidateQueries({ queryKey: ['bikes'] })
      qc.invalidateQueries({ queryKey: ['shift-open-preview'] })
      setStep('select')
    },
  })

  const closeShift = useMutation({
    mutationFn: (geo?: { lat: number; lng: number }) => api.post('/shifts/close', { geo }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift', 'current'] })
      qc.invalidateQueries({ queryKey: ['bikes'] })
      qc.invalidateQueries({ queryKey: ['shift-close-preview'] })
      setStep('select')
      setConfirmed(false)
    },
  })

  const getGeoAndRun = (action: (geo?: { lat: number; lng: number }) => void) => {
    if (!navigator.geolocation) { action(undefined); return }
    setGeoLoading(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeoError(null); setGeoLoading(false); action({ lat: pos.coords.latitude, lng: pos.coords.longitude }) },
      (err) => {
        setGeoError(err.code === 1 ? 'Геолокация запрещена. Смена будет открыта без проверки места.' : 'Геолокация недоступна.')
        setGeoLoading(false)
        action(undefined)
      },
      { timeout: 15_000, enableHighAccuracy: true },
    )
  }

  const handleConfirmOpen = () => {
    if (shiftActionRef.current) return
    shiftActionRef.current = true
    // cashAtOpen is auto-computed on the backend from last shift; we don't pass it here
    getGeoAndRun((geo) => openShift.mutate(
      { pointId: selectedPointId, geo },
      { onSettled: () => { shiftActionRef.current = false } },
    ))
  }

  const handleConfirmClose = () => {
    if (!confirmed) return
    if (shiftActionRef.current) return
    shiftActionRef.current = true
    getGeoAndRun((geo) => closeShift.mutate(
      geo,
      { onSettled: () => { shiftActionRef.current = false } },
    ))
  }

  const shift: Shift | null = shiftData?.shift ?? null

  if (isLoading) return <div className="flex items-center justify-center h-64 text-gray-400">Загрузка...</div>

  // ── CLOSE CONFIRM SCREEN ─────────────────────────────────
  if (shift && step === 'close-confirm') {
    const cp = closePreview
    const fmt = (n: number) => n.toLocaleString('ru-RU') + ' ₽'
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setStep('select')} className="text-gray-500 text-xl px-3 py-2 -ml-3 rounded-xl active:bg-gray-100">←</button>
          <h2 className="font-bold text-lg">Закрытие смены</h2>
        </div>

        {closePreviewLoading && <p className="text-gray-400 text-sm text-center py-4">Загрузка данных...</p>}

        {cp && (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">📊 Выручка за смену</p>
              <div className="grid grid-cols-3 gap-2">
                {[['💵 Наличные', cp.revenue.cash], ['💳 Безнал', cp.revenue.card], ['📲 Переводы', cp.revenue.transfer]].map(([label, val]) => (
                  <div key={String(label)} className="bg-gray-50 rounded-xl px-2 py-3 text-center">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="font-bold text-sm mt-1">{fmt(Number(val))}</p>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-sm font-semibold">Итого</span>
                <span className="text-lg font-bold text-green-700">{fmt(cp.revenue.total)}</span>
              </div>
              <div className="flex justify-between items-center bg-amber-50 rounded-xl px-3 py-2">
                <span className="text-sm text-amber-700">💰 Наличных в кассе</span>
                <span className="font-bold text-amber-800">{fmt(cp.cashBalance)}</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">📦 Инвентарь на точке</p>
              <InventoryAtPointSummary bikes={cp.bikes} />
            </div>

            {/* Active daily rentals */}
            {cp.activeDailyRentals?.length > 0 && (
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-4">
                <p className="text-sm font-semibold text-blue-700 mb-2">📅 Суточные аренды на ходу ({cp.activeDailyRentals.length})</p>
                <div className="space-y-1">
                  {cp.activeDailyRentals.map((r: { bikeCode: string; tariffName: string; plannedReturnAt: string; basePrice: number }) => (
                    <div key={r.bikeCode} className="flex justify-between text-xs text-gray-600">
                      <span>#{r.bikeCode} · {r.tariffName} · до {formatTenantDateTime(r.plannedReturnAt, { day: '2-digit', month: '2-digit' })}</span>
                      <span className="font-semibold">{r.basePrice.toLocaleString('ru-RU')} ₽</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment lines summary */}
            {cp.paymentLines && cp.paymentLines.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">📋 Сводка операций</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-100">
                      <th className="text-left pb-1">Операция</th>
                      <th className="text-left pb-1">Инвентарь</th>
                      <th className="text-left pb-1">Способ</th>
                      <th className="text-right pb-1">Сумма</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {(cp.paymentLines as Array<{ type: string; bikeCodes: string; amount: number; paymentMethod?: string | null }>).map((pl, i) => (
                      <tr key={i}>
                        <td className="py-1 text-gray-600">{({ prepayment: 'Предоплата', topup: 'Оплата', refund: 'Возврат' } as Record<string, string>)[pl.type] ?? pl.type}</td>
                        <td className="py-1 text-gray-500">#{pl.bikeCodes}</td>
                        <td className="py-1 text-gray-500">{pl.paymentMethod ? ({ cash: '💵 Нал', card: '💳 Безнал', transfer: '📲 Перевод' } as Record<string, string>)[pl.paymentMethod] ?? pl.paymentMethod : '—'}</td>
                        <td className={`py-1 text-right font-semibold ${pl.amount < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                          {pl.amount < 0 ? `−${fmt(-pl.amount)}` : fmt(pl.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="border-t pt-2 flex justify-between text-sm font-bold mt-2">
                  <span>Итого</span>
                  <span className="text-green-700">{fmt(cp.revenue.total)}</span>
                </div>
              </div>
            )}

            {cp.canClose === false && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <p className="text-sm font-semibold text-red-700">
                  ⚠️ Закрытие смены недоступно: {cp.blockingRentalsCount} активн{cp.blockingRentalsCount === 1 ? 'ая почасовая или просроченная суточная аренда' : 'ых почасовых или просроченных суточных аренд'}.
                </p>
                <p className="text-xs text-red-600 mt-1">Закройте аренды или дождитесь возврата.</p>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-xs text-gray-400 mb-1">Аренд за смену: {cp.rentalCount}</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
                  className="w-5 h-5 rounded mt-0.5 text-brand-600 shrink-0" />
                <span className="text-sm text-gray-700">
                  Подтверждаю сдачу смены. Все данные проверены и соответствуют действительности.
                </span>
              </label>
            </div>
          </>
        )}

        {geoError && <p className="text-xs text-orange-600 text-center">{geoError}</p>}
        {closeShift.isError && (
          <p className="text-red-600 text-sm text-center">
            {(closeShift.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка закрытия'}
          </p>
        )}

        <button
          onClick={handleConfirmClose}
          disabled={!confirmed || closeShift.isPending || geoLoading || cp?.canClose === false}
          className="w-full bg-red-600 text-white font-bold py-4 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {(closeShift.isPending || geoLoading) ? (
            <>
              <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {geoLoading ? 'Определяем геолокацию...' : 'Закрытие смены...'}
            </>
          ) : (
            '🔒 Закрыть смену'
          )}
        </button>
      </div>
    )
  }

  // ── SHIFT OPEN CONFIRM SCREEN ────────────────────────────
  if (!shift && step === 'open-confirm') {
    const op = openPreview
    const selectedPoint = pointsData?.points?.find((p: Point) => p.id === selectedPointId)
    return (
      <div className="p-4 space-y-4 pb-32">
        <div className="flex items-center gap-3">
          <button onClick={() => setStep('select')} className="text-gray-500 text-xl px-3 py-2 -ml-3 rounded-xl active:bg-gray-100">←</button>
          <h2 className="font-bold text-lg">Открытие смены</h2>
        </div>
        <p className="text-sm text-gray-500">Точка: <strong>{selectedPoint?.name}</strong></p>
        <p className="text-xs text-gray-400">{formatTenantDateTime(new Date().toISOString(), { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>

        {previewLoading && <p className="text-gray-400 text-sm text-center py-4">Загрузка данных...</p>}

        {op && (
          <>
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">📦 Инвентарь на точке</p>
              <InventoryAtPointSummary bikes={op.bikes} />
            </div>

            {op.inLongRental?.length > 0 && (
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-4">
                <p className="text-sm font-semibold text-blue-700 mb-2">📅 Длительная аренда с этой точки ({op.inLongRental.length})</p>
                <div className="space-y-1">
                  {op.inLongRental.map((r: { bike: { code: string; type: string }; customer: { phone: string } }) => (
                    <p key={r.bike.code} className="text-xs text-gray-600">
                      #{r.bike.code} {TYPE_LABELS[r.bike.type] ?? r.bike.type} · {r.customer.phone}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {op.todayBookings?.length > 0 && (
              <div className="bg-yellow-50 rounded-2xl border border-yellow-200 p-4">
                <p className="text-sm font-semibold text-yellow-700 mb-2">📌 Брони на сегодня ({op.todayBookings.length})</p>
                <div className="space-y-1">
                  {op.todayBookings.map((b: { id: string; bike: { code: string; type: string }; scheduledStartAt: string; customerPhone: string }) => (
                    <p key={b.id} className="text-xs text-gray-600">
                      #{b.bike.code} · {formatTenantTime(b.scheduledStartAt)} · {b.customerPhone}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
              <p className="text-sm font-semibold text-amber-700 mb-1">💰 Наличных в кассе на начало смены</p>
              <p className="text-2xl font-bold text-amber-800 text-center py-2">
                {pointCash !== null ? pointCash.toLocaleString('ru-RU') + ' ₽' : '—'}
              </p>
              <p className="text-xs text-amber-600 text-center">Остаток с предыдущей смены на этой точке</p>
            </div>
          </>
        )}

        {geoError && <p className="text-xs text-orange-600 text-center">{geoError}</p>}
        {openShift.isError && (
          <p className="text-red-600 text-sm text-center">
            {(openShift.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка открытия'}
          </p>
        )}

        <div className="fixed bottom-0 inset-x-0 p-4 bg-white border-t border-gray-200">
        <button
          onClick={handleConfirmOpen}
          disabled={openShift.isPending || geoLoading}
          className="w-full bg-brand-600 text-white font-bold py-4 rounded-2xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {(openShift.isPending || geoLoading) ? (
            <>
              <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {geoLoading ? 'Определяем геолокацию...' : 'Открытие смены...'}
            </>
          ) : (
            '✅ Подтвердить и открыть смену'
          )}
        </button>
        </div>
      </div>
    )
  }

  // ── MAIN SCREEN ──────────────────────────────────────────
  return (
    <div className="p-4">
      {shift ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="font-semibold text-green-700">Смена открыта</span>
            </div>
            <p className="font-bold text-lg">{shift.point.name}</p>
            <p className="text-sm text-gray-500">{shift.point.address ?? ''}</p>
            <p className="text-xs text-gray-400 mt-1">
              Открыта: {formatTenantTime(shift.openedAt)}
            </p>
            {shift.cashAtOpen != null && (
              <p className="text-xs text-amber-600 mt-0.5">💰 Касса при открытии: {Number(shift.cashAtOpen).toLocaleString('ru-RU')} ₽</p>
            )}
            {shift.openGeoValid === false && (
              <p className="text-xs text-orange-600 mt-1">⚠️ Смена открыта вне зоны точки</p>
            )}
          </div>

          <button
            onClick={() => setStep('close-confirm')}
            className="w-full border-2 border-red-300 text-red-600 font-semibold py-3 rounded-2xl hover:bg-red-50 transition-colors"
          >
            Закрыть смену
          </button>

          <Link
            to="/manager/shifts"
            className="block w-full text-center border border-gray-300 text-gray-700 font-medium py-3 rounded-2xl hover:bg-gray-50 transition-colors"
          >
            📋 Мои смены
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-bold">Открыть смену</h2>

          {geoError && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm text-orange-700">
              ⚠️ {geoError}
            </div>
          )}

          <div className="space-y-2">
            {pointsData?.points?.map((p: Point) => (
              <button
                key={p.id}
                onClick={() => setSelectedPointId(p.id)}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-colors ${
                  selectedPointId === p.id ? 'border-brand-600 bg-brand-50' : 'border-gray-200 bg-white'
                }`}
              >
                <p className="font-semibold">{p.name}</p>
              </button>
            ))}
          </div>

          {selectedPointId && (
            <button
              onClick={() => setStep('open-confirm')}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 rounded-2xl text-lg transition-colors"
            >
              Далее →
            </button>
          )}

          <Link
            to="/manager/shifts"
            className="block w-full text-center border border-gray-300 text-gray-700 font-medium py-3 rounded-2xl hover:bg-gray-50 transition-colors"
          >
            📋 Мои смены
          </Link>
        </div>
      )}
    </div>
  )
}
